import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { JobFilter } from '@ironsight/types'
import type { BatchListSort } from './useBatchSort'

export const createFilteredStore = (storeName: string) =>
  defineStore(
    storeName,
    () => {
      const filters = ref<JobFilter[]>([])
      const pendingFilter = ref<JobFilter | undefined>(undefined)

      const getFilters = computed<JobFilter[]>(() => filters.value)

      const getAllFilters = computed<JobFilter[]>(() => {
        if (pendingFilter.value !== undefined) {
          const idx = filters.value.findIndex((f) => f.id === pendingFilter.value!.id)
          if (idx !== -1) {
            const updated = [...filters.value]
            updated[idx] = pendingFilter.value
            return updated
          }
          return [pendingFilter.value, ...filters.value]
        }
        return filters.value
      })

      const getVisibleFilters = computed<JobFilter[]>(() => {
        let visible: JobFilter[]

        if (pendingFilter.value !== undefined) {
          const idx = filters.value.findIndex((f) => f.id === pendingFilter.value!.id)
          if (idx !== -1) {
            const updated = [...filters.value]
            updated[idx] = pendingFilter.value
            visible = updated
          } else {
            visible = [pendingFilter.value, ...filters.value]
          }
        } else {
          visible = filters.value
        }

        return visible.filter((f) => !f.hidden)
      })

      function setPendingFilter(filter: JobFilter | undefined) {
        pendingFilter.value = filter
      }

      function setFilter(filter: JobFilter) {
        pendingFilter.value = undefined

        const index = filters.value.findIndex((f) => f.id === filter.id)
        if (index !== -1) {
          filters.value.splice(index, 1, { ...filters.value[index], ...filter })
        } else {
          filters.value.push(filter)
        }
      }

      function removeFilter(id: string) {
        const index = filters.value.findIndex((f) => f.id === id)
        if (index !== -1) {
          filters.value.splice(index, 1)
        } else if (id === pendingFilter.value?.id) {
          pendingFilter.value = undefined
        }
      }

      function clearFilters() {
        filters.value = []
        pendingFilter.value = undefined
      }
      const DEFAULT_ITEMS_PER_PAGE = 20

      // do not export currentPage, itemsPerPage since we don't want to persist them in localstorage
      const currentPage = ref<number>(1)
      const itemsPerPage = ref<number>(DEFAULT_ITEMS_PER_PAGE)

      const getCurrentPage = computed(() => currentPage.value)
      const getItemsPerPage = computed(() => itemsPerPage.value)

      function updatePage(page: number) {
        currentPage.value = page
      }

      function updateItemsPerPage(items: number) {
        itemsPerPage.value = items
        currentPage.value = 1
      }
      const DEFAULT_SORT: BatchListSort = {
        field: 'Created',
        direction: 'desc',
      }

      const sort = ref<BatchListSort>(DEFAULT_SORT)

      function updateSorting(newSort: BatchListSort) {
        sort.value = newSort
      }

      const getVisibleFilterById = computed(() => {
        return (id: string) => getVisibleFilters.value.find((filter) => filter.id === id)
      })

      return {
        pendingFilter,
        getAllFilters,
        getVisibleFilters,
        getFilters,
        setFilter,
        removeFilter,
        setPendingFilter,
        filters,
        clearFilters,
        getCurrentPage,
        getItemsPerPage,
        updatePage,
        updateItemsPerPage,
        getVisibleFilterById,
        sort,
        updateSorting,
      }
    },
    {
      persist: true,
    },
  )

export const useFilteredBatchStore = createFilteredStore('filtered-batches')
